import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  /** Main empty state title. Shown in bold. */
  title: string;
  /** Optional short description. Shown in muted color below the title. */
  message?: string;
  /** Optional action (e.g. a button or link). Rendered below the message. */
  action?: ReactNode;
  /**
   * Optional icon, rendered in a tile above the title. Pass a Lucide element;
   * it is sized by the tile. Without one the state is just centred prose,
   * which reads as a rendering failure rather than a deliberate blank.
   */
  icon?: ReactNode;
  /** Optional class for the container. */
  className?: string;
}

export function EmptyState({
  title,
  message,
  action,
  icon,
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        // px-6 matters on a phone: the message could otherwise run edge to
        // edge. py is smaller on mobile because an empty state inside a tab
        // panel was spending a third of the viewport on whitespace.
        "flex flex-col items-center justify-center gap-3 px-6 py-10 text-center sm:py-14",
        className
      )}
    >
      {icon != null && (
        <div
          className="mb-1 grid h-12 w-12 place-items-center rounded-full bg-[var(--fx-s-tile)] text-[var(--fx-ink-700)] dark:text-[var(--dk-text-soft)] [&_svg]:h-5 [&_svg]:w-5"
          aria-hidden
        >
          {icon}
        </div>
      )}
      <p className="text-base font-bold text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
        {title}
      </p>
      {message != null && message !== "" && (
        <p className="max-w-md text-sm text-[var(--fx-ink-700)] dark:text-[var(--dk-text-soft)]">
          {message}
        </p>
      )}
      {/* Full width on mobile so the action gets a real target, hugging its
          label from sm up. */}
      {action != null && (
        <div className="mt-2 w-full max-w-xs sm:w-auto sm:max-w-none [&>*]:w-full sm:[&>*]:w-auto">
          {action}
        </div>
      )}
    </div>
  );
}
